// HTML body for "Everyone's an Agent Boss Now. Did Anyone Ask the Workers?"
// Single source of truth used by:
//   - BlogPost.js (dangerouslySetInnerHTML)
//   - scripts/prerender.mjs (noscript injection for SEO)
//
// Keep this in sync with sitemap.xml lastmod when content changes.

const L = (href, text) =>
    `<a href="${href}" target="_blank" rel="noopener noreferrer">${text}</a>`;

const html = `
<p>Open almost any artificial intelligence (AI) vendor keynote from the last 18 months and the same
promotion is waiting for you. You are no longer a writer, an analyst or a developer. You are a
manager of agents.</p>

<p>Microsoft put it in writing. Its 2025 Work Trend Index, built on a survey of 31,000 workers across
31 countries, says every employee becomes an "agent boss" and every worker will need to think like
the chief executive officer (CEO) of an agent-powered startup
(${L('https://www.microsoft.com/en-us/worklab/work-trend-index/2025-the-year-the-frontier-firm-is-born', 'Microsoft')}).</p>

<p>The usage data says the shift is real. Anthropic found that conversations where people hand off an
entire task, instead of working through it, rose from 27% to 39% of Claude.ai traffic in about nine
months. For enterprise customers on the application programming interface (API), it was 77%
(${L('https://www.anthropic.com/economic-index', 'Anthropic Economic Index')}).</p>

<p>I have spent 25 years writing software and I run agents every day. This is not a rant from the
sidelines. My problem is with the message we are sending everyone else: the future skill is
management, so skill up and start acting like a boss.</p>

<h2>Management talent is rare, and we have the data</h2>

<p>Gallup's research finds about one in ten people have high talent for managing others, and another
two in ten have basic talent. Companies pick the wrong person for the manager role 82% of the time
(${L('https://www.gallup.com/workplace/231593/why-great-managers-are-so-rare.aspx', 'Gallup')}).</p>

<p>Economists Alan Benson, Danielle Li and Kelly Shue studied almost 40,000 sales workers and found
firms systematically promote their best sellers, who then become worse managers
(${L('https://cepr.org/voxeu/columns/promotions-and-peter-principle', 'Benson, Li &amp; Shue')}).
That is the Peter Principle, measured. Telling every strong individual contributor that their next
step is "manager of agents" runs the same experiment across the entire workforce.</p>

<p>To be fair to the other side: leadership is not fixed at birth. A twin study by Richard Arvey and
colleagues attributed about 30% of leadership role occupancy to genetics, with the rest shaped by
environment
(${L('https://experts.umn.edu/en/publications/the-determinants-of-leadership-role-occupancy-genetic-and-persona/', 'Arvey et al. 2006')}).
Many people can learn parts of it. Plenty of them will never want to. Even Microsoft's own survey
shows only 21% of employees expect managing agents to be part of their job within five years,
against 36% of leaders. The appetite runs top-down.</p>

<h2>Where I had to change my own mind</h2>

<p>When I dug into the research, I found the "agent boss" pitch gets something basic wrong.
Supervising agents is barely management at all.</p>

<p>The talents Gallup describes in great managers are about motivating people, building
relationships and holding them accountable. An agent needs none of that. It needs a precise
specification and someone who can tell when the output is wrong.</p>

<p>A Microsoft Research and Carnegie Mellon study of 319 knowledge workers found generative AI shifts
effort toward verifying information, integrating responses and what the authors call "task
stewardship." Workers with more confidence in the AI applied less critical thinking. Workers with
more confidence in their own skill applied more
(${L('https://doi.org/10.1145/3706598.3713778', 'Lee et al., CHI 2025')}).</p>

<p>Read that with the backbone of the workforce in mind. I'll use a phrase that might sting: worker
bees. I mean it as the highest compliment. These are skilled people doing repeatable work under
tight quality control, and they are the ones who know the craft cold and have the discipline to
check it. They may be better suited to supervising AI output than the charismatic leader we keep
holding up as the model.</p>

<h2>Supervising machines is work humans are bad at</h2>

<p>In 1983, the psychologist Lisanne Bainbridge described the "ironies of automation." Automating a
process leaves humans with the hardest parts: monitoring for rare failures and taking over when
things break. Operators who only monitor lose the hands-on skill they need to take over
(${L('https://doi.org/10.1016/0005-1098(83)90046-8', 'Bainbridge 1983')}). A later review found
automation complacency and bias affect novices and experts alike and are not cured by simple
training (${L('https://doi.org/10.1177/0018720810376055', 'Parasuraman &amp; Manzey 2010')}).</p>

<p>The modern data rhymes. In a pre-registered experiment with 758 Boston Consulting Group
consultants, those using GPT-4 completed 12.2% more tasks, 25.1% faster, at over 40% higher quality
on work inside the AI's capability. On a task just outside it, they were 19 percentage points less
likely to get the right answer
(${L('https://mitsloan.mit.edu/sites/default/files/2023-10/SSRN-id4573321.pdf', "Dell'Acqua et al.")}).
Delegation works until you cannot tell where the edge is.</p>

<p>Our instincts are not a reliable gauge either. In a 2025 trial by Model Evaluation and Threat
Research (METR), experienced developers believed AI had made them 20% faster while measurements
showed them slower. METR now says tools have improved and that early result is out of date, but the
perception gap is the lesson that lasts
(${L('https://metr.org/blog/2025-07-10-early-2025-ai-experienced-os-dev-study/', 'METR')}).</p>

<p>So "stop producing, start orchestrating" quietly removes the practice that makes someone a
competent orchestrator.</p>

<h2>The backbone does not need a promotion</h2>

<p>The best field evidence on AI at work comes from Erik Brynjolfsson, Danielle Li and Lindsey
Raymond. Across more than 5,000 customer support agents, an AI assistant raised productivity 14% on
average and 34% for novice and lower-skilled workers. It also reduced requests for managerial
intervention (${L('https://www.nber.org/papers/w31161', 'Brynjolfsson, Li &amp; Raymond')}). Nobody
became a manager. People got better at the job and needed less managing.</p>

<p>Meanwhile, the swarm future is arriving slower than the keynotes. Gartner predicts over 40% of
agentic AI projects will be canceled by the end of 2027 and estimates only about 130 of the
thousands of agentic vendors are real
(${L('https://www.gartner.com/en/newsroom/press-releases/2025-06-25-gartner-predicts-over-40-percent-of-agentic-ai-projects-will-be-canceled-by-end-of-2027', 'Gartner')}).</p>

<h2>What I'd tell people instead</h2>

<p>The skill that matters is not "become a manager." It is knowing your craft well enough to specify
it precisely and to catch the machine when it is wrong. That is a skill skilled operators already
have, and organizations can build on it without forcing a management identity onto people who never
wanted one.</p>

<p>For leaders, that means designing roles where experienced practitioners own quality inside AI
workflows and keep doing enough of the real work to stay sharp. It also means being honest that the
"agent boss" story comes largely from companies selling agents.</p>

<p>I'm curious where you land. Has AI made your job more like management, or more like
inspection?</p>

<p><em>Andrew Schwabe is the founder of QCoda, working in AI engineering governance. Twenty-five-plus
years in engineering leadership will do that to you.</em></p>

<h2>References</h2>

<ol>
  <li>Microsoft &amp; LinkedIn. <em>2025: The Year the Frontier Firm Is Born.</em> Work Trend Index
  Annual Report, April 2025. ${L('https://www.microsoft.com/en-us/worklab/work-trend-index/2025-the-year-the-frontier-firm-is-born', 'WorkLab')}.</li>
  <li>Anthropic. <em>Anthropic Economic Index.</em> September 2025.
  ${L('https://www.anthropic.com/economic-index', 'anthropic.com')}.</li>
  <li>Gallup. <em>Why Great Managers Are So Rare.</em>
  ${L('https://www.gallup.com/workplace/231593/why-great-managers-are-so-rare.aspx', 'gallup.com')}.</li>
  <li>Alan Benson, Danielle Li, Kelly Shue. <em>Promotions and the Peter Principle.</em> Quarterly
  Journal of Economics, 2019. ${L('https://www.nber.org/papers/w24343', 'NBER working paper')};
  ${L('https://cepr.org/voxeu/columns/promotions-and-peter-principle', 'VoxEU summary')}.</li>
  <li>Richard D. Arvey et al. <em>The determinants of leadership role occupancy: Genetic and
  personality factors.</em> The Leadership Quarterly 17, 2006.
  ${L('https://experts.umn.edu/en/publications/the-determinants-of-leadership-role-occupancy-genetic-and-persona/', 'University of Minnesota')}.</li>
  <li>Hao-Ping Lee et al. <em>The Impact of Generative AI on Critical Thinking.</em> CHI 2025.
  ${L('https://doi.org/10.1145/3706598.3713778', 'ACM')}.</li>
  <li>Lisanne Bainbridge. <em>Ironies of Automation.</em> Automatica 19(6), 1983.
  ${L('https://doi.org/10.1016/0005-1098(83)90046-8', 'DOI')}.</li>
  <li>Raja Parasuraman, Dietrich H. Manzey. <em>Complacency and Bias in Human Use of Automation: An
  Attentional Integration.</em> Human Factors 52(3), 2010.
  ${L('https://doi.org/10.1177/0018720810376055', 'DOI')}.</li>
  <li>Fabrizio Dell'Acqua et al. <em>Navigating the Jagged Technological Frontier.</em> Harvard
  Business School Working Paper 24-013, 2023.
  ${L('https://mitsloan.mit.edu/sites/default/files/2023-10/SSRN-id4573321.pdf', 'Paper PDF')}.</li>
  <li>Joel Becker, Nate Rush, Beth Barnes, David Rein. <em>Measuring the Impact of Early-2025 AI on
  Experienced Open-Source Developer Productivity.</em> METR, July 2025.
  ${L('https://metr.org/blog/2025-07-10-early-2025-ai-experienced-os-dev-study/', 'METR')};
  ${L('https://metr.org/blog/2026-02-24-uplift-update/', 'February 2026 update')}.</li>
  <li>Erik Brynjolfsson, Danielle Li, Lindsey Raymond. <em>Generative AI at Work.</em> Quarterly
  Journal of Economics, 2025. ${L('https://www.nber.org/papers/w31161', 'NBER Working Paper 31161')}.</li>
  <li>Gartner. <em>Gartner Predicts Over 40% of Agentic AI Projects Will Be Canceled by End of
  2027.</em> Press release, June 25, 2025.
  ${L('https://www.gartner.com/en/newsroom/press-releases/2025-06-25-gartner-predicts-over-40-percent-of-agentic-ai-projects-will-be-canceled-by-end-of-2027', 'gartner.com')}.</li>
</ol>
`;

module.exports = html;
